import { CLOSE_BUTTON_TESTID, FALLBACK_SNAP_PCT, FOCUSABLE_SELECTORS, HANDLE_TESTID } from './bottom-sheet.constants';

type ElementWithPointerCapture = Element & {
  setPointerCapture?: (pointerId: number) => void;
  releasePointerCapture?: (pointerId: number) => void;
};

export const isButtonPointerTarget = (target: EventTarget | null): boolean =>
  target instanceof Element && target.closest('button') !== null;

export const canStartFullBleedHeaderDrag = (target: EventTarget | null, headerSelector: string): boolean =>
  target instanceof Element && target.closest(headerSelector) !== null && !isButtonPointerTarget(target);

/** SSR-safe viewport height read. Returns 0 when `window` is undefined. */
export const safeInnerHeight = (): number => (globalThis.window === undefined ? 0 : globalThis.window.innerHeight);

/** Pick the effective viewport height: prefer the React state, fall back to live measurement. */
export const resolveViewportHeight = (stateHeight: number): number => (stateHeight > 0 ? stateHeight : safeInnerHeight());

/** Best-effort pointer capture. Some environments (jsdom, older Safari) lack it. */
export const safeSetPointerCapture = (target: EventTarget | null, pointerId: number): void => {
  const el = target as ElementWithPointerCapture | null;
  if (!el || typeof el.setPointerCapture !== 'function') return;
  try {
    el.setPointerCapture(pointerId);
  } catch {
    // capture is a hint; failure is recoverable in jsdom + older Safari.
  }
};

/** Best-effort pointer release. Mirrors `safeSetPointerCapture`. */
export const safeReleasePointerCapture = (target: EventTarget | null, pointerId: number): void => {
  const el = target as ElementWithPointerCapture | null;
  if (!el || typeof el.releasePointerCapture !== 'function') return;
  try {
    el.releasePointerCapture(pointerId);
  } catch {
    // release may already have happened — both ends mean the pointer is free.
  }
};

/** Clamp an index into `snapPoints` to the valid range [0, length-1]. */
export const clampSnapIndex = (index: number, length: number): number => Math.min(Math.max(0, index), Math.max(0, length - 1));

/** Pick the snap point fraction at `index`, falling back to a safe default. */
export const snapPctAt = (snapPoints: readonly number[], index: number): number =>
  snapPoints[clampSnapIndex(index, snapPoints.length)] ?? FALLBACK_SNAP_PCT;

/** Return the snap point with the smallest distance to `currentPct`. */
export const findNearestSnap = (snapPoints: readonly number[], currentPct: number): number => {
  const first = snapPoints[0] ?? FALLBACK_SNAP_PCT;
  let nearest = first;
  let minDiff = Math.abs(currentPct - first);
  for (const sp of snapPoints) {
    const d = Math.abs(currentPct - sp);
    if (d < minDiff) {
      minDiff = d;
      nearest = sp;
    }
  }
  return nearest;
};

/** Index of the snap point closest to `currentPct`. */
export const findNearestSnapIndex = (snapPoints: readonly number[], currentPct: number): number =>
  snapPoints.reduce((best, sp, i) => (Math.abs(sp - currentPct) < Math.abs((snapPoints[best] ?? sp) - currentPct) ? i : best), 0);

/**
 * Collect focusable descendants of `root`, excluding chrome (drag handle, close button).
 * Both are tabbable so keyboard users can reach them, but they're chrome — initial focus
 * and the Tab cycle should target body content instead, otherwise "Close" gets announced first.
 */
export const getContentFocusables = (root: HTMLElement): HTMLElement[] =>
  Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS)).filter(
    el => el.dataset.testid !== HANDLE_TESTID && el.dataset.testid !== CLOSE_BUTTON_TESTID
  );
